import { MaxProfileResponse, MaxProfileTenant } from '../types'
import { MAX_RUNTIME_CONFIG } from '../config/max-env.config'
import { maxDebug } from '../utils/max-debug'

type LoginExchangeResponse = {
  chatToken?: string
  expiresIn?: number
}

export const decodeJwtExpiration = (token: string): number | null => {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = JSON.parse(atob(normalized)) as { exp?: number }
    if (typeof decoded.exp !== 'number') return null
    return decoded.exp * 1000
  } catch {
    return null
  }
}

const assertApiBaseUrl = () => {
  if (!MAX_RUNTIME_CONFIG.api.baseUrl) {
    throw new Error('MAX API base URL is not configured (REACT_APP_MAX_API).')
  }
}

export const exchangeAuth0AccessTokenForChatToken = async (
  auth0AccessToken: string
): Promise<{ chatToken: string; expiresIn: number }> => {
  assertApiBaseUrl()
  const url = `${MAX_RUNTIME_CONFIG.api.baseUrl}/login`

  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth0AccessToken}`,
      },
      body: JSON.stringify({}),
    })
  } catch (error) {
    const typedError = error instanceof Error ? error : new Error(String(error))
    maxDebug.error('POST /login - fetch failed before response', {
      url,
      name: typedError.name,
      message: typedError.message,
      hint: 'Browser TypeError here usually indicates CORS preflight failure, DNS, TLS, or network block.',
    })
    throw typedError
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '')
    maxDebug.error('POST /login - non-ok response', {
      status: response.status,
      body: errorBody || 'No response body',
    })
    throw new Error(
      `MAX /login exchange failed (${response.status}). ${errorBody || 'No response body'}`
    )
  }

  const data = (await response.json()) as LoginExchangeResponse

  if (!data.chatToken) {
    throw new Error('MAX /login exchange succeeded but response did not include chatToken.')
  }

  return {
    chatToken: data.chatToken,
    expiresIn: data.expiresIn || 900,
  }
}

export const fetchMaxProfile = async (chatToken: string): Promise<MaxProfileResponse> => {
  assertApiBaseUrl()
  const url = `${MAX_RUNTIME_CONFIG.api.baseUrl}/profile`

  let response: Response
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${chatToken}`,
      },
    })
  } catch (error) {
    const typedError = error instanceof Error ? error : new Error(String(error))
    maxDebug.error('GET /profile - fetch failed before response', {
      url,
      name: typedError.name,
      message: typedError.message,
      hint: 'This path should match MAX app behavior; verify backend CORS allowlist includes current origin.',
    })
    throw typedError
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '')
    maxDebug.error('GET /profile - non-ok response', {
      status: response.status,
      body: errorBody || 'No response body',
    })
    throw new Error(`MAX /profile failed (${response.status}). ${errorBody || 'No response body'}`)
  }

  return (await response.json()) as MaxProfileResponse
}

const toTenantRecord = (tenant: MaxProfileTenant): MaxProfileTenant | null => {
  if (tenant == null || tenant.tenantId == null) return null
  return tenant
}

export const resolveMaxTenantFromProfile = (
  profile: MaxProfileResponse,
  prodigyTenantId: string | number
): { maxTenantId: string; maxTenantName: string | null; isExploreModeEnabled: boolean } | null => {
  const availableTenants = profile.user?.availableTenants || []
  const targetProdigyTenantId = String(prodigyTenantId)

  const matched = availableTenants
    .map(toTenantRecord)
    .filter((tenant): tenant is MaxProfileTenant => tenant !== null)
    .find((tenant) => String(tenant.prodigyTenantId) === targetProdigyTenantId)

  if (!matched) return null

  return {
    maxTenantId: String(matched.tenantId),
    maxTenantName: matched.tenantName || null,
    isExploreModeEnabled: matched.enableExploreMode ?? false,
  }
}
